import { environment } from '../../../environments/environment';
import { formatInteger } from './currency.util';
import { Product, ProductCategoryRef } from '../models/product.model';
import {
  PublicCategory,
  PublicHomeBannerImage,
  PublicOfferProduct,
} from '../../features/home/data/home-blocks.model';
import { CategoryItem } from '../../features/home/data/mock-categories';
import { toUiProduct } from './product.utils';

// `environment.imagesUrl` ya termina en `/images/` (ver environment.ts). Los
// endpoints nuevos (home blocks, heroes, secciones de juego) devuelven paths
// que arrancan con `images/...`; concatenar tal cual produciría
// `/images/images/...`. Esta función strippea el prefijo redundante. Si el path
// ya es absoluto (`http://` o `https://`) lo devuelve sin tocar.
//
// Exportada porque el mapper de `/web/games` resuelve exactamente los mismos
// paths relativos (`images/game-sections/<file>`): duplicar la regla de
// normalización garantizaría que las dos copias divergieran.
export function joinImagesUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const trimmed = path.replace(/^\/+/, '');
  const withoutPrefix = trimmed.startsWith('images/')
    ? trimmed.slice('images/'.length)
    : trimmed;
  return `${environment.imagesUrl}${withoutPrefix}`;
}

/**
 * Resuelve la URL completa de una imagen de banner del home (paths que vienen
 * como `images/home-blocks/<filename>` en `hero-banner` y `stats-banner`).
 */
export function resolveHomeBannerUrl(image: PublicHomeBannerImage | null | undefined): string {
  if (!image?.imageUrl) return '';
  return joinImagesUrl(image.imageUrl);
}

/**
 * Contador de la portada (jugadores, miembros). NO es dinero, pero usa el mismo
 * separador que los importes: `formatInteger` de `currency.util`, no `Intl`, que
 * en `es-CR` emite espacio estrecho y cambia con la versión del navegador.
 *
 * Conserva el `'0'` ante ausencia de valor —un contador vacío en la portada es
 * un hueco raro—, a diferencia de `formatCrc`, que devuelve cadena vacía.
 */
export function formatHomeCount(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '0';
  return formatInteger(n);
}

/**
 * Sustituye marcadores del bloque `community` por sus valores formateados.
 * El copy del backoffice puede incluir `{jugadores}` y `{miembros}`; se
 * reemplazan por el número con separador de miles. Si el texto no trae el
 * marcador, se devuelve tal cual (el admin escribió texto literal). Cualquier
 * marcador desconocido se deja intacto.
 */
export function interpolateCommunityText(
  text: string | null | undefined,
  values: { jugadores?: number | null; miembros?: number | null },
): string {
  if (!text) return '';
  return text.replace(/\{(jugadores|miembros)\}/g, (match, key: string) => {
    const n = key === 'jugadores' ? values.jugadores : values.miembros;
    return n == null ? match : formatHomeCount(n);
  });
}

/**
 * Renderiza el valor de un stat del bloque `stats-banner`: anteponiendo
 * "+ de " cuando el backend marca `is_approximate=true`, o el número exacto
 * en caso contrario. Mantiene el formato con separadores de miles.
 */
export function formatStatValue(stat: { number: number; is_approximate: boolean }): string {
  const formatted = formatHomeCount(stat.number);
  return stat.is_approximate ? `+ de ${formatted}` : formatted;
}

/**
 * Normaliza un número de teléfono a solo dígitos para construir links a
 * WhatsApp / tel. Acepta cualquier formato del admin (con `+`, espacios,
 * paréntesis) y descarta caracteres no numéricos.
 */
export function normalizePhoneDigits(phone: string | null | undefined): string {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}

/**
 * Resuelve la URL completa de una imagen del carrusel de heroes (paths que
 * vienen como `images/heroes/<filename>` desde `GET /api/hero-carousel`).
 * Las URLs absolutas (subidas a CDN externo) pasan tal cual.
 */
export function resolveHeroImageUrl(path: string | null | undefined): string {
  if (!path) return '';
  return joinImagesUrl(path);
}

/**
 * Resuelve la URL del cover de una categoría del home. Los assets físicos
 * viven en `<images_base>/categories/covers/<filename>` — mismo path que ya
 * usa `catalog-mappers.resolveCoverUrl` para el catálogo y el megamenú. La
 * doc `home-blocks-web.md` describe un prefijo más corto (`categories/`) que
 * no coincide con la realidad del storage; ignoramos ese matiz y siempre
 * componemos la URL contra `categories/covers/`.
 *
 * Acepta cualquiera de los tres shapes posibles del backend (`Magic.png`,
 * `categories/Magic.png` o `categories/covers/Magic.png`) y normaliza para
 * que el resultado siempre sea correcto. Devuelve `null` si no hay cover —
 * el caller decide si pinta placeholder u oculta el tile.
 */
export function resolveHomeCategoryCoverUrl(cover: string | null | undefined): string | null {
  if (!cover) return null;
  if (/^https?:\/\//i.test(cover)) return cover;
  const filename = cover
    .replace(/^\/+/, '')
    .replace(/^categories\/covers\//, '')
    .replace(/^categories\//, '');
  return `${environment.imagesUrl}categories/covers/${filename}`;
}

/**
 * Adapta una `PublicCategory` del bloque `categories-carousel` al view-model
 * plano `CategoryItem` que consume el carrusel del home. Filtra (vía caller)
 * las que no tienen `slug` para no producir routerLinks rotos.
 */
export function publicCategoryToCarouselItem(c: PublicCategory): CategoryItem | null {
  if (!c.slug) return null;
  return {
    id: String(c.id),
    name: c.name,
    slug: `/tienda/${c.slug}`,
    image: resolveHomeCategoryCoverUrl(c.cover) ?? '',
  };
}

/**
 * Convierte un `PublicOfferProduct` (shape del API) al `Product` de UI que
 * consumen las cards y modales. Reutiliza `toUiProduct` — el shape público
 * es estructuralmente un subconjunto de `Product`, así que sólo necesitamos
 * normalizar las categorías (el API permite `slug: null`, pero
 * `ProductCategoryRef.slug` es requerido — descartamos refs sin slug para
 * que la PDP routing no quede colgada).
 */
export function publicOfferProductToUiProduct(p: PublicOfferProduct): Product {
  const categories: ProductCategoryRef[] = (p.categories ?? [])
    .filter((c): c is typeof c & { slug: string } => typeof c.slug === 'string')
    .map((c) => ({
      id: c.id,
      name: c.name,
      slug: c.slug,
      parent:
        c.parent && typeof c.parent.slug === 'string'
          ? { id: c.parent.id, name: c.parent.name, slug: c.parent.slug }
          : null,
    }));

  // Compose un `Product`-like que `toUiProduct` sabe digerir. Los campos no
  // presentes en `PublicOfferProduct` quedan undefined y el mapper aplica
  // defaults (currency '₡', etc.). isNew y setCode no existen en la API (D23):
  // se quedan en false y '' en vez de inventarlos.
  const apiProduct: Product = {
    id: p.id,
    name: p.name,
    slug: p.slug ?? undefined,
    image: p.image ?? undefined,
    sale_price: p.sale_price,
    taxes: p.taxes,
    discount: p.discount,
    discountQty: p.discountQty ?? undefined,
    stock: p.stock,
    quality: p.quality ?? undefined,
    product_type: p.product_type ?? undefined,
    language: p.language ?? undefined,
    rarity: p.rarity ?? undefined,
    is_foil: p.is_foil,
    is_holo: p.is_holo,
    prepurchase: p.prepurchase,
    prepurchase_stock: p.prepurchase_stock ?? undefined,
    // Restricción por Perrón Points. Esta whitelist es la que alimenta home
    // (6 bloques), landings, related y FBT: omitir un campo aquí es
    // exactamente cómo `early_access` se volvió invisible en julio de 2026.
    early_access: p.early_access,
    early_access_min_points: p.early_access_min_points,
    early_access_threshold: p.early_access_threshold,
    // U3 (§15): las 8 mecánicas con el mismo snake_case que Product y los 4
    // campos de carta de D22 con su nombre camelCase. Clave omitida, no
    // `undefined`, cuando el valor es null/ausente (sellados, caché previa).
    ...(p.color != null ? { color: p.color } : {}),
    ...(p.cost != null ? { cost: p.cost } : {}),
    ...(p.mana_cost != null ? { mana_cost: p.mana_cost } : {}),
    ...(p.power != null ? { power: p.power } : {}),
    ...(p.toughness != null ? { toughness: p.toughness } : {}),
    ...(p.subtype != null ? { subtype: p.subtype } : {}),
    ...(p.counter != null ? { counter: p.counter } : {}),
    ...(p.card_attribute != null ? { card_attribute: p.card_attribute } : {}),
    ...(p.card_type != null ? { cardType: p.card_type } : {}),
    ...(p.hp != null ? { hp: p.hp } : {}),
    ...(p.stage != null ? { stage: p.stage } : {}),
    ...(p.short_description != null ? { shortDescription: p.short_description } : {}),
    categories,
    // game y setName se derivan aquí de la categoría (D27) con el mismo
    // criterio que toUiProduct: su `??` no salta con '', así que un default
    // vacío dejaba el set sin nombre en destacado y zoom. setCode sigue ''
    // (D23); el resto son defaults UI que toUiProduct sobrescribe.
    game: categories[0]?.parent?.name ?? '',
    setName: categories[0]?.name ?? '',
    setCode: '',
    currency: '₡',
    imageUrl: '',
    variant: p.is_foil ? 'foil' : 'non-foil',
    isNew: false,
    isOnSale: false,
    isPreSale: p.prepurchase,
  };

  return toUiProduct(apiProduct);
}
